import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  Chip,
  IconButton,
  Collapse,
  Box,
  Divider,
  Alert,
  Stack
} from '@mui/material';
import {
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  NavigateNext as NavigateNextIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';

function RequiredFieldsReminder() {
  const [expandedIncomplete, setExpandedIncomplete] = useState(true);
  const [expandedConflicts, setExpandedConflicts] = useState(true);
  const [incompleteDevices, setIncompleteDevices] = useState([]);
  const [ipConflicts, setIpConflicts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    // Refresh data every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await Promise.all([
      loadIncompleteDevices(),
      loadIpConflicts()
    ]);
  };

  const loadIncompleteDevices = async () => {
    try {
      const response = await axios.get('/devices/');
      const devicesData = response.data.items || response.data;
      checkIncompleteDevices(devicesData);
    } catch (err) {
      console.error('Error loading devices for reminder:', err);
    }
  };

  const loadIpConflicts = async () => {
    try {
      const response = await axios.get('/subnets/ip-conflicts/check');
      setIpConflicts(response.data || []);
    } catch (err) {
      console.error('Error loading IP conflicts:', err);
    }
  };

  const checkIncompleteDevices = (devicesData) => {
    const incomplete = [];

    devicesData.forEach(device => {
      const missingFields = [];

      // Check required fields
      if (!device.name || device.name.trim() === '') {
        missingFields.push('Название');
      }
      if (!device.device_type_id) {
        missingFields.push('Тип устройства');
      }

      // Check for required characteristics
      if (device.device_type_characteristics) {
        device.device_type_characteristics.forEach(char => {
          if (char.is_required) {
            // Find if this characteristic has a value
            const hasValue = device.characteristic_values?.some(
              cv => cv.characteristic_id === char.id && cv.value && cv.value.trim() !== ''
            );
            if (!hasValue) {
              missingFields.push(`Характеристика: ${char.name}`);
            }
          }
        });
      }

      if (missingFields.length > 0) {
        incomplete.push({
          id: device.id,
          name: device.name || 'Без названия',
          missingFields: missingFields
        });
      }
    });

    setIncompleteDevices(incomplete);
  };

  const handleNavigateToDevice = (deviceId) => {
    navigate(`/devices/${deviceId}`);
  };

  if (incompleteDevices.length === 0 && ipConflicts.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={2}
      sx={{
        position: 'fixed',
        right: 20,
        top: 100,
        width: 350,
        maxHeight: '80vh',
        overflow: 'auto',
        zIndex: 1000
      }}
    >
      {/* IP Conflicts Section */}
      {ipConflicts.length > 0 && (
        <Paper
          sx={{
            border: '2px solid #f44336',
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#ffebee',
              cursor: 'pointer'
            }}
            onClick={() => setExpandedConflicts(!expandedConflicts)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon color="error" />
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                Конфликты IP адресов
              </Typography>
              <Chip
                label={ipConflicts.length}
                size="small"
                color="error"
              />
            </Box>
            <IconButton size="small">
              {expandedConflicts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expandedConflicts}>
            <Divider />
            <List sx={{ p: 0 }}>
              {ipConflicts.map((conflict, index) => (
                <React.Fragment key={`${conflict.subnet_id}-${conflict.ip_address}`}>
                  <ListItem
                    sx={{
                      display: 'block',
                      bgcolor: '#fff5f5'
                    }}
                  >
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="error">
                        IP: {conflict.ip_address}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Подсеть: {conflict.subnet_name}
                      </Typography>
                    </Box>
                    <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                      Используется на {conflict.conflict_count} устройствах:
                    </Alert>
                    <Stack spacing={0.5}>
                      {conflict.devices.map((device) => (
                        <Box
                          key={`${device.device_id}-${device.interface_id}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            bgcolor: 'white',
                            borderRadius: 1,
                            border: '1px solid #ffcdd2'
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {device.device_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Интерфейс: {device.interface_name}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleNavigateToDevice(device.device_id)}
                            color="primary"
                          >
                            <NavigateNextIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </ListItem>
                  {index < ipConflicts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Collapse>
        </Paper>
      )}

      {/* Incomplete Devices Section */}
      {incompleteDevices.length > 0 && (
        <Paper
          sx={{
            border: '2px solid #ff9800',
            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#fff3e0',
              cursor: 'pointer'
            }}
            onClick={() => setExpandedIncomplete(!expandedIncomplete)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                Незаполненные поля
              </Typography>
              <Chip
                label={incompleteDevices.length}
                size="small"
                color="warning"
              />
            </Box>
            <IconButton size="small">
              {expandedIncomplete ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expandedIncomplete}>
            <Divider />
            <List sx={{ p: 0 }}>
              {incompleteDevices.map((device, index) => (
                <React.Fragment key={device.id}>
                  <ListItem
                    sx={{
                      display: 'block',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {device.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleNavigateToDevice(device.id)}
                        color="primary"
                      >
                        <NavigateNextIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ pl: 1 }}>
                      {device.missingFields.map((field, idx) => (
                        <Chip
                          key={idx}
                          label={field}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                          variant="outlined"
                          color="warning"
                        />
                      ))}
                    </Box>
                  </ListItem>
                  {index < incompleteDevices.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Collapse>
        </Paper>
      )}
    </Stack>
  );
}

export default RequiredFieldsReminder;
